import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Edge, Node } from 'reactflow';
import { useDebounceEffect } from '../useDebounceEffect';
import { nodeDataAutoSaveDynamic } from './nodedata_autosave';

type SetSaving = (saving: {
  lastSaved?: string;
  status: string;
  finished?: boolean;
  flowId?: string;
}) => void;

type SetSchema = (
  schema: Record<string, { type: string; required: boolean }>,
) => void;

export interface UseNodeFieldAutoSaveOptions<T> {
  externalValue: T;
  fieldName: string;
  nodeId: string;
  flowId: string | undefined;
  edges: Edge[];
  setNodeState: Dispatch<SetStateAction<Node[]>>;
  setSaving: SetSaving;
  setSchema: SetSchema;
  changeType?: string;
  debounceMs?: number;
}

const MIN_IN_FLIGHT_GATE_MS = 1500;

export function useNodeFieldAutoSave<T>(
  opts: UseNodeFieldAutoSaveOptions<T>,
): [T, (value: T) => void] {
  const { externalValue, debounceMs = 500 } = opts;

  const [local, setLocal] = useState<T>(externalValue);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef(0);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const setLocalValue = (value: T) => {
    dirtyRef.current = true;
    setLocal(value);
  };

  useDebounceEffect(
    () => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      inFlightRef.current += 1;
      const current = optsRef.current;
      nodeDataAutoSaveDynamic({
        newEdges: current.edges,
        setNodeState: current.setNodeState,
        id: current.nodeId,
        setSaving: current.setSaving,
        flowId: current.flowId,
        changeType: current.changeType ?? 'default',
        objectData: { [current.fieldName]: local },
        setSchema: current.setSchema,
      });
      const currentDebounceMs = current.debounceMs ?? 500;
      const gateMs = Math.max(currentDebounceMs * 3, MIN_IN_FLIGHT_GATE_MS);
      setTimeout(() => {
        inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      }, gateMs);
    },
    [local],
    debounceMs,
  );

  useEffect(() => {
    if (dirtyRef.current) return;
    if (inFlightRef.current > 0) return;
    setLocal(prev => (Object.is(prev, externalValue) ? prev : externalValue));
  }, [externalValue]);

  return [local, setLocalValue];
}
